import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Crop from "../../../lib/components/custom/Crop";
import Icon from "../../../lib/components/custom/Icon";
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "../../../lib/components/ui/avatar";
import { Card } from "../../../lib/components/ui/card";
import { useApi } from "../../../lib/hooks/api/use-api";
import type { GetTypes } from "../../../lib/types/api-types";

interface ProfileInfoProps {
	session: GetTypes["auth"]["useSession"] | null;
	userProfile?: GetTypes["users"]["getCurrentUserProfile"];
	onSessionUpdate: () => void;
}

export function ProfileInfo({ session, onSessionUpdate }: ProfileInfoProps) {
	const { t } = useTranslation();
	const { uploads } = useApi();
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Crop dialog state
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [isCropDialogOpen, setIsCropDialogOpen] = useState(false);

	const uploadUserImage = uploads.uploadUserImage;
	const deleteUserImage = uploads.deleteUserImage;

	const handleCroppedImage = (croppedFile: File) => {
		// Upload the cropped image
		if (session?.user?.id) {
			uploadUserImage.mutate(
				{
					userId: session.user.id,
					image: croppedFile,
				},
				{
					onSuccess: () => {
						setIsCropDialogOpen(false);
						setSelectedFile(null);
						onSessionUpdate();
					},
				},
			);
		}
	};

	const handleCropDialogClose = () => {
		setIsCropDialogOpen(false);
		setSelectedFile(null);
		// Clear the file input
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleImageDelete = () => {
		if (session?.user?.id) {
			deleteUserImage.mutate(session.user.id, {
				onSuccess: () => {
					onSessionUpdate();
				},
			});
		}
	};

	const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			// Show crop dialog instead of uploading directly
			setSelectedFile(file);
			setIsCropDialogOpen(true);
		}
	};

	const openFileDialog = () => {
		fileInputRef.current?.click();
	};

	return (
		<>
			<Card className="p-6 text-center">
				<div className="">
					<div
						className={`relative mx-auto w-24 h-24 mb-4 group ${uploadUserImage.isPending ? "cursor-not-allowed" : "cursor-pointer"}`}
						onClick={uploadUserImage.isPending ? undefined : openFileDialog}
					>
						<Avatar className="w-24 h-24">
							<AvatarImage
								src={session?.user?.image || undefined}
								alt={t("profile.profile_info.avatar_alt")}
								className="object-cover"
							/>
							<AvatarFallback className="text-2xl bg-primary text-primary-foreground">
								{session?.user?.name
									? session.user.name.charAt(0).toUpperCase()
									: t("profile.profile_info.fallback_name")
											.charAt(0)
											.toUpperCase()}
							</AvatarFallback>
						</Avatar>
						{/* Hover overlay with camera icon */}
						<div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-full flex items-center justify-center">
							<Icon name="Camera" className="w-6 h-6 text-white" />
						</div>
						{/* Loading overlay */}
						{uploadUserImage.isPending && (
							<div className="absolute inset-0 bg-black/70 rounded-full flex items-center justify-center">
								<Icon
									name="LoaderPinwheel"
									className="w-6 h-6 text-white animate-spin"
								/>
							</div>
						)}
					</div>
					<h2 className="text-xl font-semibold">
						{session?.user?.name || t("profile.profile_info.fallback_name")}
					</h2>
					<p className="text-muted-foreground">{session?.user?.email || ""}</p>
				</div>

				{/* Hidden file input */}
				<input
					ref={fileInputRef}
					type="file"
					accept="image/jpeg,image/jpg,image/png,image/webp"
					onChange={handleImageUpload}
					className="hidden"
				/>
			</Card>

			{/* Crop Dialog */}
			{selectedFile && (
				<Crop
					isOpen={isCropDialogOpen}
					onClose={handleCropDialogClose}
					image={selectedFile}
					onCrop={handleCroppedImage}
					initialAspectRatio="square"
					allowedAspectRatios={["square"]}
				/>
			)}
		</>
	);
}
